/**
 * Authentication Context
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/lib/api';

const TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000; // Refresh every 10 minutes (token expires at 15m)

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  dashboardAccess: string[];
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: { email: string; password: string; firstName: string; lastName: string }) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(async () => {
      const refreshed = await api.tryRefreshToken();
      if (!refreshed) {
        setUser(null);
        if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      }
    }, TOKEN_REFRESH_INTERVAL);
  }, []);

  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Check for existing session or SSO callback on mount
  useEffect(() => {
    async function checkAuth() {
      // Handle SSO callback: ?sso=success&payload=...
      const params = new URLSearchParams(window.location.search);
      const ssoStatus = params.get('sso');

      if (ssoStatus === 'success') {
        const payload = params.get('payload');
        if (payload) {
          try {
            const decoded = JSON.parse(atob(payload));
            api.setTokens(decoded.tokens.accessToken, decoded.tokens.refreshToken);
            setUser(decoded.user);
            startRefreshTimer();
            // Clean up URL
            window.history.replaceState({}, '', '/dashboard');
            setIsLoading(false);
            return;
          } catch {
            // Invalid payload, fall through to normal auth check
          }
        }
      } else if (ssoStatus === 'error') {
        // Clean up URL, let login page show error via its own URL param handling
        setIsLoading(false);
        return;
      }

      const token = api.getAccessToken();
      if (token) {
        const result = await api.getMe();
        if (result.success && result.data) {
          setUser(result.data);
          startRefreshTimer();
        }
      }
      setIsLoading(false);
    }
    checkAuth();
    return () => stopRefreshTimer();
  }, [startRefreshTimer, stopRefreshTimer]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.login(email, password);

    if (result.success && result.data) {
      api.setTokens(result.data.tokens.accessToken, result.data.tokens.refreshToken);
      setUser(result.data.user);
      startRefreshTimer();
      return { success: true };
    }

    return {
      success: false,
      error: result.error?.message || 'Login failed',
    };
  }, [startRefreshTimer]);

  const register = useCallback(async (data: { email: string; password: string; firstName: string; lastName: string }) => {
    const result = await api.register(data);

    if (result.success && result.data) {
      api.setTokens(result.data.tokens.accessToken, result.data.tokens.refreshToken);
      setUser(result.data.user);
      startRefreshTimer();
      return { success: true };
    }

    return {
      success: false,
      error: result.error?.message || 'Registration failed',
    };
  }, [startRefreshTimer]);

  const logout = useCallback(async () => {
    stopRefreshTimer();
    await api.logout();
    setUser(null);
  }, [stopRefreshTimer]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
